import { DatePicker, message, Popover, Tooltip } from "antd";
import fr_FR from "antd/lib/date-picker/locale/fr_FR";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as NextIcon } from "../../../assets/img/icons/next.svg";
import { ReactComponent as PrevIcon } from "../../../assets/img/icons/prev.svg";
import rangePickerIcon from "../../../assets/img/CALENDRIER.svg";
import { setRegroupmentValue } from "../../../data/slices/categories";
import { updateProject } from "../../../data/slices/project";
import { adjustCalendarYears } from "../../../utilities/adjustCalendarYears";
import { formatMonthRange } from "../../../utilities/filters";
import {
  formatFiscalYearEndLabel,
  getExerciceBounds,
  getMultiExercicePeriod,
  getPeriodPresetFromCurrentMonth,
  normalizeDisplayedPeriodPreset,
} from "../../../utilities/periodPresets";
import { renderFiscalYearEndPickerPanel } from "../../../utilities/fiscalYearPickerPanel";

const CURRENT_MONTH_PRESETS = [
  {
    key: "3months",
    label: "Période de 3 mois (de m-1 à m+1)",
  },
  {
    key: "5months",
    label: "Période de 5 mois (de m-2 à m+2)",
  },
  {
    key: "12months",
    label: "Période de 12 mois (à partir de m-1)",
  },
];

const EXERCICE_OPTIONS = [
  {
    key: "mois",
    label: "Exercice en cours (mois par mois)",
  },
  {
    key: "trimestre",
    label: "Exercice en cours (regroupement par trimestre)",
  },
  {
    key: "3exercices",
    label: "3 exercices (36 mois) regroupés par année (de n-1 à n+1)",
  },
  {
    key: "4exercices",
    label: "4 exercices (48 mois) regroupés par année (de n-2 à n+1)",
  },
];

const PeriodAfficheePopover = ({
  selectedMonthRange,
  handleMonthChange,
  hasDivider,
  mode,
  isSidebarClosed,
}) => {
  const dispatch = useDispatch();
  const { project } = useSelector((state) => state.project);

  const [popoverVisible, setPopoverVisible] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [exerciceOpen, setExerciceOpen] = useState(false);
  const [draftRange, setDraftRange] = useState(selectedMonthRange);
  const [savingExercice, setSavingExercice] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);

  const fiscalYearEnd = project?.fiscalYearEnd || null;
  const selectedPreset = normalizeDisplayedPeriodPreset(
    project?.displayedPeriodPreset
  );


  useEffect(() => {
    setDraftRange(selectedMonthRange);
  }, [selectedMonthRange]);

  const applyPeriod = (dates, regroupmentValue = null, { closePopover = true } = {}) => {
    if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) return;
    handleMonthChange(dates);
    if (regroupmentValue != null) {
      dispatch(setRegroupmentValue(regroupmentValue));
    }
    setRangeOpen(false);
    setExerciceOpen(false);
    if (closePopover) {
      setPopoverVisible(false);
    }
  };

  const handlePresetViewClick = (presetKey) => {
    applyPeriod(getPeriodPresetFromCurrentMonth(presetKey), 1);
  };

  const handlePresetDefaultSave = async (presetKey) => {
    if (!project?.id) return;

    setSavingPreset(true);
    try {
      await dispatch(
        updateProject({
          id: Number(project.id),
          displayedPeriodPreset: presetKey,
        })
      ).unwrap();
      message.success("Choix par défaut enregistré");
    } catch (err) {
      message.error("Impossible d'enregistrer la période prédéfinie");
    } finally {
      setSavingPreset(false);
    }
  };


  const handleExerciceOptionClick = (optionKey) => {
    if (!fiscalYearEnd) {
      message.warning(
        "Définissez d'abord l'exercice en cours via « à définir »"
      );
      setExerciceOpen(true);
      return;
    }

    if (optionKey === "mois") {
      const { start, end } = getExerciceBounds(fiscalYearEnd);
      applyPeriod([start, end], 1);
      return;
    }

    if (optionKey === "trimestre") {
      const { start, end } = getExerciceBounds(fiscalYearEnd);
      applyPeriod([start, end], 3);
      return;
    }

    if (optionKey === "3exercices") {
      applyPeriod(getMultiExercicePeriod(fiscalYearEnd, 1, 1), 12);
      return;
    }

    if (optionKey === "4exercices") {
      applyPeriod(getMultiExercicePeriod(fiscalYearEnd, 2, 1), 12);
    }
  };

  const handleSaveExercice = async (value) => {
    if (!value || !project?.id) return;
    const nextValue = value.format("YYYY-MM");
    setSavingExercice(true);
    try {
      await dispatch(
        updateProject({
          id: Number(project.id),
          fiscalYearEnd: nextValue,
        })
      ).unwrap();
      message.success("Exercice en cours enregistré");
      setExerciceOpen(false);
    } catch (err) {
      message.error("Impossible d'enregistrer l'exercice en cours");
    } finally {
      setSavingExercice(false);
    }
  };

  const monthCellRender = (value) => {
    const formattedDate = dayjs(value)
      .format("MMM")
      .replace(/\./g, "")
      .slice(0, 4);
    return <div className="ant-picker-cell-inner">{formattedDate}</div>;
  };

  const dividerClassName =
    mode === "treso" ? "treso-divider" : "compta-divider";

  const content = (
    <div
      className={`period-affichee-popover ${
        mode === "compta" ? "period-affichee-popover--compta" : ""
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="period-affichee-popover__header">
        <h3>Modifier manuellement la période affichée :</h3>
        <div className="period-affichee-picker-slot">
          <button
            type="button"
            className="period-affichee-current-btn"
            onClick={() => {
              setExerciceOpen(false);
              setRangeOpen((prev) => !prev);
            }}
          >
            {formatMonthRange(selectedMonthRange)}
          </button>
          <DatePicker.RangePicker
            open={rangeOpen}
            onOpenChange={(open) => {
              setRangeOpen(open);
              if (!open && draftRange?.[0] && draftRange?.[1]) {
                applyPeriod(draftRange);
              }
            }}
            value={draftRange}
            onCalendarChange={(selectedDates, _dateStrings, info) => {
              setDraftRange(selectedDates);
              const [startDate, endDate] = selectedDates || [];
              if (startDate) {
                const year = startDate.year();
                const dropdown = document.querySelector(
                  `.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)`
                );
                const firstYear = dropdown?.querySelector(
                  ".ant-picker-year-btn"
                );
                if (firstYear) {
                  adjustCalendarYears(
                    year,
                    Number(firstYear.textContent) === year
                  );
                }
              }
              if (startDate && endDate && info?.range === "end") {
                applyPeriod(selectedDates);
              }
            }}
            format="MM"
            separator={null}
            locale={fr_FR}
            cellRender={monthCellRender}
            picker="month"
            placement="bottomRight"
            superNextIcon={<NextIcon />}
            superPrevIcon={<PrevIcon />}
            dropdownClassName="custom-RangePicker-dropdown period-affichee-range-dropdown"
            allowClear={false}
            className="period-affichee-hidden-picker"
            getPopupContainer={() => document.body}
          />
        </div>
      </div>

      <div className="period-affichee-block">
        <p className="period-affichee-block__title">
          Afficher une période prédéfinie liée au mois en cours :
        </p>
        <div
          className="period-affichee-preset-grid"
          role="radiogroup"
          aria-label="Période prédéfinie"
        >
          <div className="period-affichee-options">
            <span className="period-affichee-options__header-spacer" aria-hidden="true" />
            <p className="period-affichee-block__title period-affichee-preset-header__default-label">
              Choix par défaut
            </p>
            {CURRENT_MONTH_PRESETS.map((preset) => {
              const isSelected = selectedPreset === preset.key;
              return (
                <div key={preset.key} className="period-affichee-preset-row">
                  <button
                    type="button"
                    className="period-affichee-option-btn"
                    disabled={savingPreset}
                    onClick={() => handlePresetViewClick(preset.key)}
                  >
                    {preset.label}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    aria-label={`Définir ${preset.label} comme choix par défaut`}
                    className={`period-affichee-default-choice ${
                      isSelected ? "is-selected" : ""
                    }`}
                    disabled={savingPreset}
                    onClick={() => handlePresetDefaultSave(preset.key)}
                  >
                    <span className="period-affichee-radio" aria-hidden="true">
                      <span className="period-affichee-radio__dot" />
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="period-affichee-block">
        <div className="period-affichee-block__title-row">
          <p className="period-affichee-block__title">
            Afficher une période prédéfinie, liée à
            <br />
            l&apos;exercice en cours à clôturer à la fin du mois de :
          </p>
          <div className="period-affichee-picker-slot period-affichee-fiscal-slot">
            <button
              type="button"
              className="period-affichee-current-btn period-affichee-exercice-btn"
              disabled={savingExercice}
              onClick={() => {
                setRangeOpen(false);
                setExerciceOpen((prev) => !prev);
              }}
            >
              {formatFiscalYearEndLabel(fiscalYearEnd)}
            </button>
            <DatePicker
              open={exerciceOpen}
              onOpenChange={setExerciceOpen}
              value={
                fiscalYearEnd ? dayjs(fiscalYearEnd, "YYYY-MM") : null
              }
              onChange={handleSaveExercice}
              picker="month"
              locale={fr_FR}
              format="MMM YYYY"
              allowClear={false}
              disabled={savingExercice}
              className="period-affichee-hidden-picker"
              dropdownClassName="period-affichee-fiscal-dropdown"
              panelRender={renderFiscalYearEndPickerPanel}
              placement="bottomRight"
              getPopupContainer={() => document.body}
              superNextIcon={<NextIcon />}
              superPrevIcon={<PrevIcon />}
            />
          </div>
        </div>
        <div className="period-affichee-options period-affichee-exercice-options">
          {EXERCICE_OPTIONS.map((option) => (
            <div key={option.key} className="period-affichee-preset-row">
              <button
                type="button"
                className={`period-affichee-option-btn ${
                  !fiscalYearEnd ? "is-disabled" : ""
                }`}
                onClick={() => handleExerciceOptionClick(option.key)}
              >
                {option.label}
              </button>
              <span
                className="period-affichee-row-spacer"
                aria-hidden="true"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="period-affichee-nav-item">
      <Popover
        color={mode === "treso" ? "#fffa7d" : "#a9efe5"}
        placement="right"
        align={{ offset: [12, 0] }}
        content={content}
        visible={popoverVisible}
        trigger="click"
        onVisibleChange={(visible) => {
          if (!visible && (rangeOpen || exerciceOpen)) return;
          setPopoverVisible(visible);
          if (!visible) {
            setRangeOpen(false);
            setExerciceOpen(false);
          }
        }}
        overlayClassName="period-affichee-popover-overlay"
        destroyTooltipOnHide
      >
        <Tooltip
          trigger="hover"
          mouseLeaveDelay={0}
          mouseEnterDelay={0.8}
          title={isSidebarClosed ? "Période Affichée" : null}
        >
          <button
            type="button"
            className="button nav-link period-affichee-trigger"
          >
            <div className="range-picker-button-wrapper">
              <img
                src={rangePickerIcon}
                alt="Période Affichée"
                id="range-id"
              />
              {!isSidebarClosed && (
                <span className="input-label-range-picker">
                  <p>Période Affichée</p>
                </span>
              )}
            </div>
          </button>
        </Tooltip>
      </Popover>
      {hasDivider && <div className={`divider ${dividerClassName}`} />}
    </div>
  );
};

export default PeriodAfficheePopover;
